import { Alert, Button, Card, Stack, Text, TextInput, Title } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { unwrap } from '../api/client';
import { createOrganization } from '../api/gen';
import { useSession } from '../lib/session';

/** A slug the server accepts: lower-case letters, digits and single hyphens. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/, '');
}

/**
 * What a first-time caller sees: they belong to no organisation, and every
 * route but this one needs one. Founding it makes them its owner; the new
 * organisation is then the one they act in.
 */
export function CreateOrganization() {
  const { setOrgId } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');

  // The slug follows the name until somebody edits it.
  const effectiveSlug = slug ?? slugify(name);

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        createOrganization({
          body: { name: name.trim(), slug: effectiveSlug, displayName: displayName.trim() },
        }),
      ),
    onSuccess: async (out) => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setOrgId(out.organization.id);
    },
  });

  const valid = name.trim() !== '' && displayName.trim() !== '' && effectiveSlug.length >= 2;

  return (
    <Card withBorder p="xl" maw={440}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
      >
        <Stack>
          <Title order={4}>Create your organisation</Title>
          <Text c="dimmed" size="sm">
            You are not a member of any organisation yet. Create one to begin; you will be its
            owner.
          </Text>
          <TextInput
            label="Organisation name"
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <TextInput
            label="Slug"
            description="Lower-case letters, digits and hyphens"
            required
            maxLength={64}
            value={effectiveSlug}
            onChange={(e) => setSlug(e.currentTarget.value)}
          />
          <TextInput
            label="Your name"
            description="How you appear inside the organisation"
            required
            maxLength={200}
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
          />
          {create.error && (
            <Alert color="red" variant="light" role="alert">
              {create.error.message}
            </Alert>
          )}
          <Button type="submit" disabled={!valid} loading={create.isPending}>
            Create organisation
          </Button>
        </Stack>
      </form>
    </Card>
  );
}
